import React, { useState, useCallback } from "react";
import { Box, TextField } from "@material-ui/core";
import {
  MobileAutocompleteOptions,
  ActionButton,
  InfoIcon,
  Icon,
  IconName,
} from "halifax";
import clsx from "clsx";

import "./styles.scss";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ICategorizedResponse,
  IResult,
  LocationQueryEnum,
  RecentExperienceSearch,
} from "redmond";
import { useLazyGetExperiencesDestinationQuery } from "@capone/common";

import {
  AVAILABLE,
  EXPERIENCES_SEARCH_NEARBY_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
} from "../../../context/experiments";
import { getSelectionName } from "../../../utils/selectionHelpers";
import { TripDestination } from "../../search/types";
import { TripDestinationsSection } from "../../search/components/TripDestinationsSection";

export const LOADING_TEXT = "Loading";
export const CONTINUE = "Continue";

export interface ILocationSearchProps {
  location: IResult | null;
  locationCategories: ICategorizedResponse[];
  loading: boolean;
  hasLocationAutocompleteError: boolean;
  setLocation: (location: IResult | null) => void;
  fetchLocationCategories: (queryString: string) => void;
  setLocationAutocompleteError: (error: boolean) => void;
  onComplete?: (value?: any) => void;
  recentSearches?: RecentExperienceSearch[];
  onRecentSearchClick?: (search: RecentExperienceSearch) => void;
  onClickAutocompleteError?: () => void;
  locationSearchPrefixString: string;
  continueOnSelect?: boolean;
  tripDestinations?: TripDestination[];
  tripDestinationsLoading?: boolean;
  onTripClick?: (trip: TripDestination, index: number) => void;
  onFocusSearch?: () => void;
  onBlurSearch?: () => void;
  onSearchInput?: (queryString: string) => void;
  onLocationSelected?: (value: IResult) => void;
  onSearchNearbyClicked?: () => void;
}

export const LocationSearch = (props: ILocationSearchProps) => {
  const {
    location,
    loading,
    onComplete,
    setLocation,
    fetchLocationCategories,
    locationCategories,
    hasLocationAutocompleteError,
    setLocationAutocompleteError,
    recentSearches,
    onRecentSearchClick,
    onClickAutocompleteError,
    locationSearchPrefixString,
    continueOnSelect = true,
    tripDestinations,
    tripDestinationsLoading,
    onTripClick,
    onFocusSearch,
    onBlurSearch,
    onSearchInput,
    onLocationSelected: onLocationSelectedCallback,
    onSearchNearbyClicked,
  } = props;

  const [fetchExpDestinations, { data: destinationData }] =
    useLazyGetExperiencesDestinationQuery();

  const [locationSearchString, setLocationSearchString] = useState<string>(
    location?.label || ""
  );
  const [labelToDisplay, setLabelToDisplay] = useState<string>(
    locationSearchPrefixString
  );

  const expState = useExperiments();
  const isSearchNearbyExperiment =
    getExperimentVariant(
      expState.experiments,
      EXPERIENCES_SEARCH_NEARBY_EXPERIMENT
    ) === AVAILABLE;

  const searchNearbyLocationResult = destinationData?.categories[0].results[0];

  const searchNearby = useCallback((latitude: number, longitude: number) => {
    fetchExpDestinations({
      query: {
        LocationQuery: LocationQueryEnum.Coordinate,
        lat: latitude,
        lon: longitude,
      },
    });
  }, []);

  const handleSearchNearbyClick = useCallback(() => {
    if (searchNearbyLocationResult && onComplete) {
      onSearchNearbyClicked?.();
      onComplete(searchNearbyLocationResult);
    }
  }, [searchNearbyLocationResult, onComplete, onSearchNearbyClicked]);

  const onLocationSearch = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (hasLocationAutocompleteError) setLocationAutocompleteError(false);

    setLocation(null);

    setLocationSearchString(event.target.value);
    if (event.target.value) {
      onSearchInput?.(event.target.value);
      fetchLocationCategories(event.target.value);
    }
  };

  const onContinue = (value?: any) => {
    if (onComplete) {
      onComplete(value);
    }
  };

  const onLocationSelected = (value: any) => {
    onLocationSelectedCallback?.(value);
    setLocationSearchString(value.label);
    setLocation(value);
    if (continueOnSelect) onContinue(value);
  };

  const handleFocus = () => {
    onFocusSearch?.();
    setLabelToDisplay(locationSearchPrefixString);
  };

  const handleBlur = () => {
    onBlurSearch?.();
    setLabelToDisplay(locationSearchPrefixString);
  };

  const onClearLocationField = () => {
    setLocation(null);
    setLocationSearchString("");
  };

  return (
    <Box className="mobile-search-location">
      <Box className="mobile-location-pickers">
        <Box className="mobile-search-location-input-field">
          <Icon name={IconName.B2BMapPin} />
          <TextField
            className={clsx("origin-auto-complete", "mobile")}
            onChange={(value) => onLocationSearch(value)}
            value={locationSearchString}
            label={labelToDisplay}
            InputProps={{
              disableUnderline: true,
            }}
            onFocus={handleFocus}
            onBlur={!locationSearchString ? handleBlur : undefined}
          />
          {/* Note: Instead of conditionally rendering this icon component, a className of 'hidden' is conditionally used so it can take the style "visibility:hidden"
            This style will allocate space for the icon even when it's not shown - this prevents the sibling elements from changing when it's shown or hidden */}
          <FontAwesomeIcon
            className={clsx("icon", { hidden: !locationSearchString })}
            icon={faTimesCircle}
            onClick={onClearLocationField}
          />
        </Box>
        {hasLocationAutocompleteError && (
          <Box className="autocomplete-error-container">
            <InfoIcon />
            <span className="autocomplete-error-text">
              Error fetching results.{" "}
            </span>
            <span onClick={onClickAutocompleteError} className="try-again-text">
              Try again.
            </span>
          </Box>
        )}
      </Box>

      <MobileAutocompleteOptions
        value={null}
        valueCategories={locationCategories}
        setValue={(value: any) => onLocationSelected(value)}
        loading={loading}
        loadingText={LOADING_TEXT}
        hasSearchValue={locationSearchString.length > 0}
      />
      {!loading &&
        locationSearchString.length === 0 &&
        (onTripClick || onRecentSearchClick) && (
          <TripDestinationsSection
            tripDestinations={onTripClick ? (tripDestinations || []) : []}
            loading={onTripClick ? (tripDestinationsLoading || false) : false}
            onTripClick={onTripClick || (() => {})}
            recentSearches={recentSearches}
            onRecentSearchClick={onRecentSearchClick}
            searchNearby={isSearchNearbyExperiment ? searchNearby : undefined}
            searchNearbyLocation={
              searchNearbyLocationResult?.id
                ? getSelectionName(searchNearbyLocationResult.id)
                : undefined
            }
            handleSearchNearbyClick={handleSearchNearbyClick}
          />
        )}
      {!!location && (
        <Box className="mobile-packages-search-buttons-container">
          <ActionButton
            className="mobile-autocomplete-continue-button"
            message={CONTINUE}
            onClick={() => {
              onContinue(location);
            }}
          />
        </Box>
      )}
    </Box>
  );
};
